import { ColorBadge, Theme, WorkflowStepper } from '@spoiler-alert/ui-library';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Route, Switch } from 'react-router-dom';
import Tracking from '../../utilities/tracking-apis';

// The Command Center workflow tabs. Visual chrome is provided by the shared ui-library
// WorkflowStepper; this wrapper handles the routing concerns (active key from URL,
// redirect-to-first-step when landing on a disabled URL, Route/Switch outlet for stepChild)
// and the Command Center-specific badge / metrics / disabled-flag mapping.
const useStyles = createUseStyles({
  metricDisabled: { color: Theme.grey80 },
});

export const Stepper = ({ steps, match, history, user }) => {
  const classes = useStyles();
  const basePath = match.path;

  const activeStep = steps.find((s) => history.location.pathname.includes(`${basePath}/${s.link}`));
  const activeKey = activeStep ? activeStep.link : '';

  // Redirect when the user lands on the base path or on a disabled step's URL.
  if (history.location.pathname === basePath || (activeStep && activeStep.enabled === false)) {
    history.push(`${basePath}/${steps[0].link}`);
  }

  const stepperSteps = useMemo(
    () =>
      steps.map((step) => {
        const isDisabled = step.enabled === false;
        const showBadge = step.badgeStatus && step.enabled && !step.enterpriseDisabled && !step.saasLiteDisabled;
        let badge = null;
        if (showBadge && step.badgeStatus === 'completed') badge = <ColorBadge theme="green">Complete</ColorBadge>;
        if (showBadge && step.badgeStatus === 'task') badge = <ColorBadge theme="red">1</ColorBadge>;

        // Metrics: render with the muted "disabled" treatment when the step is
        // enterprise/SaaS-Lite restricted (except for "Negotiate", which keeps full color).
        const metricsDisabled = (step.enterpriseDisabled || step.saasLiteDisabled) && step.title !== 'Negotiate';
        const metrics = step.metrics
          ? {
              value: <span className={metricsDisabled ? classes.metricDisabled : ''}>{step.metrics.value}</span>,
              label: step.metrics.label,
            }
          : undefined;

        return {
          key: step.link,
          label: step.title,
          icon: step.icon ? <step.icon /> : undefined,
          disabled: isDisabled,
          disabledReason: isDisabled ? step.toolTipText : undefined,
          badge,
          metrics,
        };
      }),
    [steps, classes.metricDisabled],
  );

  const trackEvent = (link) => {
    const step = steps.find((s) => s.link === link);
    if (!step) return;
    Tracking.Event(`${step.title} tab clicked`, {
      stepTitle: step.title,
      timestamp: new Date().toISOString(),
      email: user?.email,
      transactionCycleId: user?.site?.transactionCycleId,
    });
  };

  const handleChange = (key) => {
    trackEvent(key);
    history.push(`${basePath}/${key}`);
  };

  const stepContents = steps.map((step, index) => {
    if (step.enabled === false) return null;
    return (
      <Route key={index} exact path={`${basePath}/${step.link}`}>
        {step.stepChild}
      </Route>
    );
  });

  return (
    <div>
      <WorkflowStepper steps={stepperSteps} active={activeKey} onChange={handleChange} />
      <Switch>{stepContents}</Switch>
    </div>
  );
};

Stepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      icon: PropTypes.elementType,
      badgeStatus: PropTypes.string,
      metrics: PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }),
      enabled: PropTypes.bool,
      toolTipText: PropTypes.string,
      stepChild: PropTypes.node,
      enterpriseDisabled: PropTypes.bool,
      saasLiteDisabled: PropTypes.bool,
    }),
  ).isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  user: PropTypes.object,
};
